import { getPagePath } from '@/utils/pagePaths';
import { getMediaType } from '@/utils/media';
import { withTextSlide } from '@/slideshow/text';

// Re-export shared layout utilities for use by components
export {
    ANCHOR_POSITIONS,
    SCALE_VALUES,
    FILL_SCALE_VALUES,
    DEFAULT_SPREAD_SETTINGS,
    getAnchorStyles,
    getObjectPosition,
    calculateSpreadLayout,
    calculatePagePercentages,
    getImageStyle,
    calculateUnifiedPageSize as calculateUnifiedPageSizeBase,
} from '../spreadLayoutUtils';

// Import for local use
import {
    DEFAULT_SPREAD_SETTINGS,
    calculateUnifiedPageSize as calculateUnifiedPageSizeBase,
    isPosition,
} from '../spreadLayoutUtils';

const positionOf = (item) => (isPosition(item?.x) && isPosition(item?.y) ? { x: item.x, y: item.y } : null);

// Animation and timing constants
export const FADE_DURATION = 200; // 200ms fade duration
// Page open/close opacity fades (viewer entrance, layout overlay, editorial
// intro). Off for editorial ↔ directory navigation; flip to true to restore.
export const ENABLE_VIEWER_PAGE_FADES = false;
export const ONBOARDING_SWIPE_ANIMATION_DURATION = 500; // 500ms swipe animation duration
export const ONBOARDING_DURATION = 2000; // 2000ms onboarding duration

// Swipe-to-close constants
export const SWIPE_CONFIG = {
    MIN_VERTICAL: 10,
    MIN_SWIPE_CLOSE: 80,
    SCALE_DISTANCE: 400,
    SCALE_MAX: 0.25,
    ANIMATE_MS: 250,
};

/**
 * Check if an item from images array is a spread
 * @param {*} item - Item from images array
 * @returns {boolean}
 */
export const isSpread = (item) => Array.isArray(item) && item.length === 2;

/**
 * Normalize a spread image item (can be string or object)
 * @param {string|Object} item - Image ID string or { id, scale?, anchor?, fill? }
 * @returns {Object} Normalized spread image: { id, scale, anchor, fill }
 */
export const normalizeSpreadImage = (item) => {
    if (typeof item === 'string') {
        return {
            id: item,
            ...DEFAULT_SPREAD_SETTINGS,
        };
    }
    return {
        id: item.id,
        scale: item.scale || DEFAULT_SPREAD_SETTINGS.scale,
        anchor: item.anchor || DEFAULT_SPREAD_SETTINGS.anchor,
        fill: typeof item.fill === 'boolean' ? item.fill : DEFAULT_SPREAD_SETTINGS.fill,
        ...positionOf(item),
    };
};

/**
 * Flatten images array - spreads become individual images
 * Used for mobile where images are shown one at a time
 * @param {Array} images - Raw images array (can contain spreads)
 * @returns {Array} Flat array of image IDs
 */
export const flattenImages = (images) => {
    if (!Array.isArray(images)) return [];
    const result = [];
    for (const item of images) {
        if (isSpread(item)) {
            // Extract IDs from spread
            for (const img of item) {
                const id = typeof img === 'string' ? img : img.id;
                result.push(id);
            }
        } else {
            // Single image - could be string or object
            const id = typeof item === 'string' ? item : item.id;
            result.push(id);
        }
    }
    return result;
};

/**
 * Generates image viewer URL from page and image ID
 * @param {string|null} page - Page slug or full path
 * @param {string} img - Image ID
 * @param {Object} pagesObj - Pages object from content
 * @returns {string} URL for the image viewer
 */
export const toUrl = (page, img, pagesObj) => {
    if (!page) return `/${img}.img`;
    // If page is a full path already, use it
    if (page.includes('/')) {
        return `/${page}/${img}.img`;
    }
    // Otherwise, find the page and get its full path
    if (pagesObj && pagesObj[page]) {
        const fullPath = getPagePath(pagesObj[page], pagesObj);
        return `/${fullPath}/${img}.img`;
    }
    return `/${page}/${img}.img`;
};

/**
 * Generates a dedicated video URL (ending in `.vid`) from page and video ID.
 * Parallels {@link toUrl} but uses the `.vid` suffix so the public router can
 * resolve it to the autoplay video player.
 * @param {string|null} page - Page slug or full path
 * @param {string} vid - Video file ID
 * @param {Object} pagesObj - Pages object from content
 * @returns {string} URL for the dedicated video player
 */
export const toVideoUrl = (page, vid, pagesObj) => {
    if (!page) return `/${vid}.vid`;
    if (page.includes('/')) {
        return `/${page}/${vid}.vid`;
    }
    if (pagesObj && pagesObj[page]) {
        const fullPath = getPagePath(pagesObj[page], pagesObj);
        return `/${fullPath}/${vid}.vid`;
    }
    return `/${page}/${vid}.vid`;
};

/**
 * Parse video ID and slug from URL/match params (for `.vid` routes).
 * @param {Object} match - React Router match object
 * @param {Object} location - React Router location object
 * @returns {{ videoId: string|undefined, slug: string|null }}
 */
export const parseVideoParams = (match, location) => {
    let videoId = match?.params?.video;
    let slug = match?.params?.slug;

    if (!videoId && (location?.pathname || match?.url)) {
        const url = location?.pathname || match?.url || '';
        const pathString = url.replace(/^\//, '');
        const segments = pathString ? pathString.split('/') : [];
        const lastSegment = segments[segments.length - 1] || '';

        if (lastSegment.endsWith('.vid')) {
            videoId = lastSegment.replace('.vid', '');
            slug = segments.length > 1 ? segments.slice(0, -1).join('/') : null;
        }
    }

    return { videoId, slug };
};

/**
 * Parse image ID and slug from URL/match params
 * @param {Object} match - React Router match object
 * @param {Object} location - React Router location object
 * @returns {{ imageId: string|undefined, slug: string|null }}
 */
export const parseImageParams = (match, location) => {
    let imageId = match?.params?.image;
    let slug = match?.params?.slug;
    
    if (!imageId && (location?.pathname || match?.url)) {
        const url = location?.pathname || match?.url || '';
        const pathString = url.replace(/^\//, '');
        const segments = pathString ? pathString.split('/') : [];
        const lastSegment = segments[segments.length - 1] || '';
        
        if (lastSegment.endsWith('.img')) {
            imageId = lastSegment.replace('.img', '');
            slug = segments.length > 1 ? segments.slice(0, -1).join('/') : null;
        }
    }
    
    return { imageId, slug };
};

/**
 * Get optimized image style object
 * @param {Object} imageData - Image data with dimensions
 * @returns {Object} Style object for optimized rendering
 */
export const getOptimizedImageStyle = (imageData) => ({
    imageRendering: '-webkit-optimize-contrast',
    maxWidth: imageData?.dimensions?.width 
        ? `min(100%, ${imageData.dimensions.width}px)` 
        : '100%',
    maxHeight: imageData?.dimensions?.height 
        ? `min(100%, ${imageData.dimensions.height}px)` 
        : '100%',
});

/**
 * Prepare slides data for Swiper (touch navigation) - flattens spreads for mobile
 * @param {Array} images - Array of image IDs or spreads
 * @param {Object} files - Files object from content
 * @param {string|null} page - Current page slug
 * @param {Object} pagesObj - Pages object from content
 * @returns {Array} Prepared slides data (flattened - no spreads)
 */
export const prepareSlidesData = (images, files, page, pagesObj) => {
    if (!images || !files) return [];
    
    // Flatten images (spreads become individual images)
    const flatImages = flattenImages(images);
    
    return flatImages.map(imgId => {
        const file = files[imgId];
        if (!file) return null;
        const video = getMediaType(file) === 'video';
        return {
            id: imgId,
            type: video ? 'video' : 'image',
            file,
            url: video ? toVideoUrl(page, imgId, pagesObj) : toUrl(page, imgId, pagesObj),
        };
    }).filter(Boolean);
};

/**
 * Prepare slides data for desktop - keeps spreads grouped
 * @param {Array} images - Array of image IDs or spreads
 * @param {Object} files - Files object from content
 * @param {string|null} page - Current page slug
 * @param {Object} pagesObj - Pages object from content
 * @returns {Array} Prepared slides data (with spreads)
 */
export const prepareDesktopSlidesData = (images, files, page, pagesObj) => {
    if (!images || !files) return [];
    
    return images.map((item, index) => {
        if (isSpread(item)) {
            // It's a spread - prepare both images
            const [first, second] = item.map(normalizeSpreadImage);
            const firstFile = files[first.id];
            const secondFile = files[second.id];
            
            if (!firstFile || !secondFile) return null;
            
            return {
                id: `spread-${index}`,
                type: 'spread',
                images: [
                    {
                        ...first,
                        file: firstFile,
                        url: toUrl(page, first.id, pagesObj),
                    },
                    {
                        ...second,
                        file: secondFile,
                        url: toUrl(page, second.id, pagesObj),
                    },
                ],
                // URL for the spread goes to the first image
                url: toUrl(page, first.id, pagesObj),
            };
        } else {
            // Single image - may have scale/anchor/fill settings
            const imgId = typeof item === 'string' ? item : item.id;
            const scale = typeof item === 'object' ? item.scale : undefined;
            const anchor = typeof item === 'object' ? item.anchor : undefined;
            const fill = typeof item === 'object' ? item.fill : undefined;
            const file = files[imgId];
            if (!file) return null;
            const video = getMediaType(file) === 'video';
            return {
                id: imgId,
                type: video ? 'video' : 'image',
                file,
                url: video ? toVideoUrl(page, imgId, pagesObj) : toUrl(page, imgId, pagesObj),
                // Include scale, anchor, and fill if set
                ...(scale !== undefined && { scale }),
                ...(anchor !== undefined && { anchor }),
                ...(fill !== undefined && { fill }),
                ...(typeof item === 'object' ? positionOf(item) : null),
            };
        }
    }).filter(Boolean);
};

/**
 * Prepare slides data with editorial text as first slide (for mobile editorial mode)
 * Flattens spreads since mobile shows images one at a time
 * @param {Array} images - Array of image IDs or spreads
 * @param {Object} files - Files object from content
 * @param {string|null} page - Current page slug
 * @param {Object} pagesObj - Pages object from content
 * @param {boolean} hasText - Whether the slideshow has text to show
 * @param {'first'|'last'|null} slot - Where the text slide goes; null for none
 * @returns {Array} Prepared slides data with the text slide in its slot
 */
export const prepareEditorialSlidesData = (images, files, page, pagesObj, hasText, slot = 'first') => (
    withTextSlide(prepareSlidesData(images, files, page, pagesObj), page, hasText, slot)
);


/**
 * Prepare slides data with editorial text for desktop (keeps spreads)
 * @param {Array} images - Array of image IDs or spreads
 * @param {Object} files - Files object from content
 * @param {string|null} page - Current page slug
 * @param {Object} pagesObj - Pages object from content
 * @param {boolean} hasText - Whether the slideshow has text to show
 * @param {'first'|'last'|null} slot - Where the text slide goes; null for none
 * @returns {Array} Prepared slides data with the text slide in its slot
 */
export const prepareDesktopEditorialSlidesData = (images, files, page, pagesObj, hasText, slot = 'first') => (
    withTextSlide(prepareDesktopSlidesData(images, files, page, pagesObj), page, hasText, slot)
);

/**
 * Find the slide index containing a specific image ID
 * Handles both flat slides and spreads
 * @param {Array} slidesData - Prepared slides data
 * @param {string} imageId - Image ID to find
 * @returns {number} Slide index, or -1 if not found
 */
export const findSlideIndexByImageId = (slidesData, imageId) => {
    for (let i = 0; i < slidesData.length; i++) {
        const slide = slidesData[i];
        if (slide.type === 'spread') {
            // Check both images in spread
            if (slide.images.some(img => img.id === imageId)) {
                return i;
            }
        } else if (slide.id === imageId) {
            return i;
        }
    }
    return -1;
};

/**
 * Get combined description for a spread, avoiding repetition
 * - If both images have the same description, return it once
 * - If only one has a description, return that one
 * - If both have different descriptions, combine with separator
 * - If neither has a description, return empty string
 * 
 * @param {Object} spreadData - Spread data with images array
 * @returns {string} Combined description
 */
export const getSpreadDescription = (spreadData) => {
    if (!spreadData?.images || spreadData.images.length !== 2) return '';
    
    const [left, right] = spreadData.images;
    const leftDesc = left?.file?.description || '';
    const rightDesc = right?.file?.description || '';
    
    // Neither has description
    if (!leftDesc && !rightDesc) return '';
    
    // Same description - return once
    if (leftDesc === rightDesc) return leftDesc;
    
    // Only one has description
    if (!leftDesc) return rightDesc;
    if (!rightDesc) return leftDesc;
    
    // Both have different descriptions - combine
    return `${leftDesc}  ·  ${rightDesc}`;
};

/**
 * Calculate unified page dimensions from all slides data (website version)
 * Wrapper around shared calculateUnifiedPageSize with website-specific dimension extraction
 * 
 * @param {Array} slidesData - Array of prepared slides data
 * @returns {{ width: number, height: number, aspectRatio: number }}
 */
export const calculateUnifiedPageSize = (slidesData) => {
    // Website data structure: dimensions are at item.file.dimensions or item.images[].file.dimensions
    const getDimensions = (item) => item.file?.dimensions || null;
    return calculateUnifiedPageSizeBase(slidesData, getDimensions);
};
